import type { ISupportChatApi, MessageWire } from './api/client';
import type { TranscriptEntry } from './flow/types';

/**
 * Streams the transcript to the conversation archive (POST /messages). Bubbles are
 * numbered by their position; after "back" the tail is rewritten, so the sync remembers
 * how far it got and tells the server where the transcript was cut. Batches are sent a
 * moment after the transcript settles, and on pagehide via sendBeacon. Personal data is
 * redacted on the server - the archive never stores it.
 */
export class TranscriptSync {
	private sentCount = 0;
	/** What the server has, by position - lets a rewritten tail be detected even when "back" and the next bubble arrive together. */
	private sent: TranscriptEntry[] = [];
	private truncateFrom: number | null = null;
	private timer: number | null = null;
	private conversationId: string | null = null;
	private transcript: TranscriptEntry[] = [];

	constructor(private readonly api: ISupportChatApi, private readonly delayMs = 1500) {
		if (typeof window !== 'undefined') {
			window.addEventListener('pagehide', () => this.flush());
		}
	}

	/** A new conversation id means a new archive - everything visible is sent again from zero. */
	setConversation(id: string | null) {
		if (id === this.conversationId) {
			return;
		}
		this.conversationId = id;
		this.sentCount = 0;
		this.sent = [];
		this.truncateFrom = null;
		if (id) {
			this.schedule();
		}
	}

	/** Call whenever the transcript changed (new bubble, back, restart). */
	update(transcript: TranscriptEntry[]) {
		this.transcript = transcript;
		let same = 0;
		while (same < this.sentCount && same < transcript.length && isSameEntry(transcript[same], this.sent[same])) {
			same++;
		}
		if (same < this.sentCount) {
			// the tail was rewritten (back / restart) - the server drops everything from here and gets the new tail
			this.truncateFrom = this.truncateFrom == null ? same : Math.min(this.truncateFrom, same);
			this.sentCount = same;
			this.sent.length = same;
		}
		this.schedule();
	}

	flush() {
		if (this.timer != null) {
			clearTimeout(this.timer);
			this.timer = null;
		}
		if (!this.conversationId) {
			return;
		}
		const pending = this.transcript.slice(this.sentCount);
		if (!pending.length && this.truncateFrom == null) {
			return;
		}

		const messages: MessageWire[] = pending.map((m, i) => ({
			Seq: this.sentCount + i,
			Role: m.role,
			FlowId: m.flowId,
			StepId: m.stepId,
			Kind: m.kind ?? 'text',
			Text: m.text,
			Ts: m.ts,
		}));
		const truncateFrom = this.truncateFrom;
		this.sent = this.transcript.slice();
		this.sentCount = this.transcript.length;
		this.truncateFrom = null;
		void this.api.messages({ ConversationId: this.conversationId, TruncateFrom: truncateFrom, Messages: messages });
	}

	private schedule() {
		if (typeof window === 'undefined' || !this.conversationId) {
			return;
		}
		if (this.timer != null) {
			clearTimeout(this.timer);
		}
		this.timer = window.setTimeout(() => this.flush(), this.delayMs);
	}
}

function isSameEntry(a: TranscriptEntry, b: TranscriptEntry | undefined): boolean {
	return !!b && a.text === b.text && a.stepId === b.stepId && a.role === b.role && a.ts === b.ts;
}
